import type { CustomerMasterContract, CustomerMasterCustomer, CustomerMasterReport } from '@vy/types';
import { isNotEmpty } from '@vy/array';
import type { FilterLineType } from '../../../../components/new-design/vyTableFilter';
import { matchesStringSelection, matchesText } from '../../reports-components/shared-filter-fields';
import { formatDueDate } from '../../../../utils/format-due-date';
import { CUSTOMER_MASTER_FILTER_FIELD_IDS } from './customer-master-filter-field-ids';

type CustomerLevelMatcher = (customer: CustomerMasterCustomer, filterValue: unknown) => boolean;
type ContractLevelMatcher = (contract: CustomerMasterContract, filterValue: unknown) => boolean;

const CUSTOMER_LEVEL_MATCHERS: Record<string, CustomerLevelMatcher> = {
  [CUSTOMER_MASTER_FILTER_FIELD_IDS.CustomerName]: (customer, filterValue) =>
    matchesText(customer.customerName, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.CustomerStatus]: (customer, filterValue) =>
    matchesStringSelection(customer.status ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.CustomerId]: (customer, filterValue) =>
    matchesText(customer.customerId ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.LegalName]: (customer, filterValue) =>
    matchesText(customer.legalName ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.CustomerAlias]: (customer, filterValue) =>
    matchesText(customer.customerAlias ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.DueDate]: (customer, filterValue) =>
    matchesText(formatDueDate(customer.dueDate), filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.ParentCustomer]: (customer, filterValue) =>
    matchesStringSelection(customer.parentCustomer ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.CrmId]: (customer, filterValue) =>
    matchesText(customer.crmId ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.NsErpId]: (customer, filterValue) =>
    matchesText(customer.nsErpId ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.QbErpId]: (customer, filterValue) =>
    matchesText(customer.qbErpId ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.XeroErpId]: (customer, filterValue) =>
    matchesText(customer.xeroErpId ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.StripeId]: (customer, filterValue) =>
    matchesText(customer.stripeId ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.Subsidiary]: (customer, filterValue) =>
    matchesStringSelection(customer.subsidiary ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.Country]: (customer, filterValue) =>
    matchesStringSelection(customer.address?.country ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.CustomerType]: (customer, filterValue) =>
    matchesStringSelection(customer.customerType ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.CustomerCurrency]: (customer, filterValue) =>
    matchesStringSelection(customer.customerCurrency ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.TaxMethod]: (customer, filterValue) =>
    matchesStringSelection(customer.taxMethod ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.ManualReview]: (customer, filterValue) =>
    matchesStringSelection(
      customer.manualReview === undefined ? null : String(customer.manualReview),
      filterValue,
    ),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.PaymentProvider]: (customer, filterValue) =>
    matchesStringSelection(customer.paymentProvider ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.AutoPay]: (customer, filterValue) =>
    matchesStringSelection(
      customer.autoPay === undefined ? null : String(customer.autoPay),
      filterValue,
    ),
};

const CONTRACT_LEVEL_MATCHERS: Record<string, ContractLevelMatcher> = {
  [CUSTOMER_MASTER_FILTER_FIELD_IDS.ContractName]: (contract, filterValue) =>
    matchesText(contract.contractName, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.ContractStatus]: (contract, filterValue) =>
    matchesStringSelection(contract.status, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.ContractId]: (contract, filterValue) =>
    matchesText(contract.contractId, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.Currency]: (contract, filterValue) =>
    matchesStringSelection(contract.currency, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.AutoRenewal]: (contract, filterValue) =>
    matchesStringSelection(String(contract.autoRenewal), filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.PurchaseOrder]: (contract, filterValue) =>
    matchesText(contract.purchaseOrder ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.AccountManager]: (contract, filterValue) =>
    matchesStringSelection(contract.accountManager ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.SalesforceOpportunityId]: (contract, filterValue) =>
    matchesText(contract.salesforceOpportunityId ?? null, filterValue),

  [CUSTOMER_MASTER_FILTER_FIELD_IDS.NetSuiteSalesOrderId]: (contract, filterValue) =>
    matchesText(contract.netSuiteSalesOrderId ?? null, filterValue),
};

/**
 * Applies the active filters to the report.
 *
 * Customer-level filters drop entire customers. Contract-level filters drop
 * the non-matching contracts AND drop any customer whose contract list is
 * fully filtered out — except when no contract-level filters are active, in
 * which case contract-less customers stay visible.
 */
export function applyCustomerMasterFilters(
  report: CustomerMasterReport,
  filters: FilterLineType[],
): CustomerMasterReport {
  if (!isNotEmpty(filters)) {
    return report;
  }

  const customerFilters = filters.filter((filter) => filter.field in CUSTOMER_LEVEL_MATCHERS);
  const contractFilters = filters.filter((filter) => filter.field in CONTRACT_LEVEL_MATCHERS);

  return report.flatMap((customer) => {
    if (!customerMatchesAllFilters(customer, customerFilters)) {
      return [];
    }

    if (contractFilters.length === 0) {
      return [customer];
    }

    const matchingContracts = customer.contracts.filter((contract) =>
      contractMatchesAllFilters(contract, contractFilters),
    );

    if (matchingContracts.length === 0) {
      return [];
    }

    return [{ ...customer, contracts: matchingContracts }];
  });
}

function customerMatchesAllFilters(customer: CustomerMasterCustomer, filters: FilterLineType[]): boolean {
  return filters.every((filter) => {
    const matcher = CUSTOMER_LEVEL_MATCHERS[filter.field];
    return matcher ? matcher(customer, filter.value) : true;
  });
}

function contractMatchesAllFilters(contract: CustomerMasterContract, filters: FilterLineType[]): boolean {
  return filters.every((filter) => {
    const matcher = CONTRACT_LEVEL_MATCHERS[filter.field];
    return matcher ? matcher(contract, filter.value) : true;
  });
}
